(() => {
  const reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Track per-section cleanup functions so theme-editor unload can fire them.
  const sectionCleanups = new WeakMap();

  // ===== INTERACTIVE CR MONOGRAM (canvas ASCII grid) =====
  function initInteractiveMark(root) {
    const wrap = root.querySelector('.cr-mark-wrap');
    const canvas = root.querySelector('.cr-hero-mark');
    if (!wrap || !canvas) return null;
    const ctx = canvas.getContext('2d');

    let SIZE = wrap.offsetWidth;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function setSize() {
      SIZE = wrap.offsetWidth;
      canvas.width = SIZE * dpr;
      canvas.height = SIZE * dpr;
      canvas.style.width = SIZE + 'px';
      canvas.style.height = SIZE + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    let cells = [];
    let imgLoaded = false;
    let rafId = null;

    function buildCells(img) {
      setSize();
      const off = document.createElement('canvas');
      off.width = SIZE;
      off.height = SIZE;
      const offCtx = off.getContext('2d');
      offCtx.fillStyle = '#fff';
      offCtx.fillRect(0, 0, SIZE, SIZE);
      const pad = SIZE * 0.05;
      const drawSize = SIZE - pad * 2;
      offCtx.drawImage(img, pad, pad, drawSize, drawSize);

      // Original cell density — denser grid felt cluttered, lost the institutional look.
      const STEP = Math.max(7, Math.floor(SIZE / 80));
      const data = offCtx.getImageData(0, 0, SIZE, SIZE).data;
      const chars = 'CRCRCR0123456789·/+-=*';

      cells = [];
      for (let y = STEP / 2; y < SIZE; y += STEP) {
        for (let x = STEP / 2; x < SIZE; x += STEP) {
          const i = (Math.floor(y) * SIZE + Math.floor(x)) * 4;
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (brightness < 60) {
            cells.push({
              ox: x, oy: y, cx: x, cy: y,
              char: chars[Math.floor(Math.random() * chars.length)],
              flicker: Math.random() < 0.02 ? Math.random() * 2 + 1 : 0
            });
          }
        }
      }
    }

    function renderStatic() {
      ctx.clearRect(0, 0, SIZE, SIZE);
      const fontSize = Math.max(8, SIZE / 80);
      ctx.font = `${fontSize}px "Geist Mono", "JetBrains Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(26, 22, 18, 0.92)';
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        ctx.fillText(c.char, c.ox, c.oy);
      }
    }

    const monogramSrc = canvas.dataset.monogramSrc;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgLoaded = true;
      try {
        buildCells(img);
        if (reduced) renderStatic();
      } catch (e) {
        console.warn('camprigid hero: buildCells failed, rendering fallback', e);
        renderFallback();
      }
    };
    img.onerror = () => {
      console.warn('camprigid hero: monogram image failed to load, using text fallback');
      renderFallback();
    };
    img.src = monogramSrc;

    function renderFallback() {
      setSize();
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.font = `bold ${SIZE / 6}px "Instrument Serif", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(26, 22, 18, 0.92)';
      ctx.fillText('CR', SIZE / 2, SIZE / 2);
    }

    let mouseX = -9999, mouseY = -9999;
    let targetMX = -9999, targetMY = -9999;

    function setTarget(clientX, clientY) {
      const rect = wrap.getBoundingClientRect();
      targetMX = clientX - rect.left;
      targetMY = clientY - rect.top;
    }
    function resetTarget() {
      targetMX = -9999;
      targetMY = -9999;
    }

    wrap.addEventListener('mousemove', (e) => setTarget(e.clientX, e.clientY));
    wrap.addEventListener('mouseleave', resetTarget);

    // Touch interaction — finger drag distorts the ASCII grid the same as cursor.
    // touch-action: none on .cr-mark-wrap (set in CSS @768 / @480) keeps the browser
    // from hijacking the gesture for native scroll while the user is dragging here.
    let activeTouchId = null;
    wrap.addEventListener('touchstart', (e) => {
      if (e.touches.length === 0) return;
      const t = e.touches[0];
      activeTouchId = t.identifier;
      setTarget(t.clientX, t.clientY);
    }, { passive: true });

    wrap.addEventListener('touchmove', (e) => {
      if (activeTouchId == null) return;
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        if (t.identifier === activeTouchId) {
          setTarget(t.clientX, t.clientY);
          if (e.cancelable) e.preventDefault();
          return;
        }
      }
    }, { passive: false });

    function endTouch(e) {
      if (activeTouchId == null) return;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === activeTouchId) return; // still down
      }
      activeTouchId = null;
      resetTarget();
    }
    wrap.addEventListener('touchend', endTouch, { passive: true });
    wrap.addEventListener('touchcancel', endTouch, { passive: true });

    // Ambient glitch — subtle background flicker. Just 2–3 cells at a time,
    // every 500–850ms. Same visual density as the original mouse-driven distortion's
    // light edge, not the whole monogram swimming.
    const chars = 'CRCRCR0123456789·/+-=*';
    let lastAmbientFrame = 0;
    let nextAmbientGap = 36; // frames; ~600ms at 60fps
    const ambientJitter = new Map(); // cell index -> {dx, dy, decayUntilFrame}

    // Click/tap burst — fires harder than ambient on pointerdown anywhere in the wrap.
    // 6–10 cells flicker for ~500ms, then decay back to ambient.
    function fireBurst() {
      if (cells.length === 0) return;
      const fs = Math.max(8, SIZE / 80);
      const numCells = 6 + Math.floor(Math.random() * 5); // 6–10
      for (let g = 0; g < numCells; g++) {
        const idx = Math.floor(Math.random() * cells.length);
        cells[idx].char = chars[Math.floor(Math.random() * chars.length)];
        ambientJitter.set(idx, {
          dx: (Math.random() - 0.5) * fs * 1.6,
          dy: (Math.random() - 0.5) * fs * 1.6,
          decayUntilFrame: frame + 30 // ~500ms
        });
      }
    }
    wrap.addEventListener('pointerdown', fireBurst);

    let frame = 0;
    function render() {
      frame++;
      if (!imgLoaded) {
        rafId = requestAnimationFrame(render);
        return;
      }
      mouseX += (targetMX - mouseX) * 0.18;
      mouseY += (targetMY - mouseY) * 0.18;

      ctx.clearRect(0, 0, SIZE, SIZE);
      const fontSize = Math.max(8, SIZE / 80);
      ctx.font = `${fontSize}px "Geist Mono", "JetBrains Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const RADIUS = SIZE * 0.28;
      const PUSH = SIZE * 0.05;

      // Subtle ambient glitch — 2–3 cells per tick, every 30–50 frames (~500–850ms).
      // Idle if the eased mouse position is still parked at the off-screen sentinel.
      const idle = mouseX < -1000 && mouseY < -1000;
      if (idle && cells.length > 0 && (frame - lastAmbientFrame) >= nextAmbientGap) {
        lastAmbientFrame = frame;
        nextAmbientGap = 30 + Math.floor(Math.random() * 21); // 30–50 frames
        const numCells = 2 + Math.floor(Math.random() * 2); // 2–3 cells
        for (let g = 0; g < numCells; g++) {
          const idx = Math.floor(Math.random() * cells.length);
          cells[idx].char = chars[Math.floor(Math.random() * chars.length)];
          ambientJitter.set(idx, {
            dx: (Math.random() - 0.5) * fontSize * 0.6,
            dy: (Math.random() - 0.5) * fontSize * 0.6,
            decayUntilFrame: frame + 14
          });
        }
      }
      // Burst entries decay naturally via decayUntilFrame — no force-clear, so
      // a click during cursor-active state still gets its full ~500ms flash.

      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const dx = cell.ox - mouseX;
        const dy = cell.oy - mouseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const force = Math.max(0, 1 - dist / RADIUS);

        const tx = cell.ox + (dist > 0 ? (dx / dist) * force * PUSH : 0);
        const ty = cell.oy + (dist > 0 ? (dy / dist) * force * PUSH : 0);

        cell.cx += (tx - cell.cx) * 0.22;
        cell.cy += (ty - cell.cy) * 0.22;

        // Per-cell ambient jitter offset, decays linearly to zero
        let ambDX = 0, ambDY = 0;
        const j = ambientJitter.get(i);
        if (j) {
          if (frame >= j.decayUntilFrame) {
            ambientJitter.delete(i);
          } else {
            const remain = (j.decayUntilFrame - frame) / 14;
            ambDX = j.dx * remain;
            ambDY = j.dy * remain;
          }
        }

        let color;
        if (force > 0.02) {
          const lightness = 35 + force * 30;
          const hue = 18 - force * 4;
          color = `hsl(${hue}, 70%, ${lightness}%)`;
        } else if (j) {
          // Ambient-glitched cell flashes brown briefly
          color = 'rgba(149, 119, 91, 0.78)';
        } else if (cell.flicker > 0 && Math.sin(frame * 0.04 + cell.ox * 0.01) > 0.7) {
          color = 'rgba(149, 119, 91, 0.7)';
        } else {
          color = 'rgba(26, 22, 18, 0.92)';
        }
        ctx.fillStyle = color;
        ctx.fillText(cell.char, cell.cx + ambDX, cell.cy + ambDY);
      }
      rafId = requestAnimationFrame(render);
    }

    if (!reduced) render();

    function onResize() { if (imgLoaded) buildCells(img); if (reduced) renderStatic(); }
    window.addEventListener('resize', onResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }

  // ===== EYEBROW TYPE-IN =====
  function initTypeIn(root) {
    const target = root.querySelector('.cr-hero-eyebrow .text');
    const caret = root.querySelector('.cr-hero-eyebrow .caret');
    if (!target) return;
    const text = target.dataset.eyebrowText || '';
    if (!text) return;

    if (reduced) {
      target.textContent = text;
      if (caret) caret.classList.add('done');
      return;
    }

    let i = 0;
    function step() {
      if (i <= text.length) {
        target.textContent = text.slice(0, i);
        i++;
        setTimeout(step, 45 + Math.random() * 40);
      } else {
        setTimeout(() => caret && caret.classList.add('done'), 1200);
      }
    }
    setTimeout(step, 600);
  }

  // ===== SCRAMBLE EFFECTS =====
  function scrambleText(el, finalText, duration) {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$!#@%';
    const startTime = performance.now();
    const dur = duration || 800;

    function tick() {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / dur);
      const lockedCount = Math.floor(progress * finalText.length);
      let out = '';
      for (let i = 0; i < finalText.length; i++) {
        if (i < lockedCount) {
          out += finalText[i];
        } else if (finalText[i] === ' ' || finalText[i] === '_' || finalText[i] === '.') {
          out += finalText[i];
        } else {
          out += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      el.textContent = out;
      if (progress < 1) requestAnimationFrame(tick);
    }
    tick();
  }

  function initScrambleOnView(root) {
    if (reduced || !('IntersectionObserver' in window)) {
      root.querySelectorAll('.cr-scramble').forEach(el => {
        el.textContent = el.dataset.final || el.textContent;
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.dataset.scrambled) {
          entry.target.dataset.scrambled = '1';
          scrambleText(entry.target, entry.target.dataset.final, 700);
        }
      });
    }, { threshold: 0.5 });
    root.querySelectorAll('.cr-scramble').forEach(el => observer.observe(el));
  }

  function initScrambleOnHover(root) {
    if (reduced) return;
    root.querySelectorAll('.cr-scramble-code').forEach(el => {
      const card = el.closest('.cr-show-card');
      if (!card) return;
      card.addEventListener('mouseenter', () => {
        scrambleText(el, el.dataset.final, 500);
      });
    });
  }

  // ===== MOBILE NAV TOGGLE =====
  function initMobileToggle(root) {
    const toggle = root.querySelector('.cr-mobile-toggle');
    const menu = root.querySelector('.cr-mobile-menu');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => menu.classList.toggle('is-open'));
  }

  // ===== PDP GALLERY THUMB SWITCHING =====
  function initPdpGallery(root) {
    const main = root.querySelector('.cr-pdp-gallery-main');
    const mainImg = main && main.querySelector('img[data-pdp-main-image]');
    const frameNum = root.querySelector('.cr-pdp-frame-num');
    const thumbs = root.querySelectorAll('.cr-pdp-thumb');
    if (!main || !thumbs.length) return;
    const total = String(thumbs.length).padStart(2, '0');
    // Honor the section's frame_label_prefix setting (defaults to "Frame") instead of hardcoding it.
    const framePrefix = (frameNum && frameNum.dataset.prefix) || 'Frame';

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        const img = t.dataset.img;
        const frame = t.dataset.frame || '01';
        thumbs.forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
        if (frameNum) frameNum.textContent = `${framePrefix} ${frame} / ${total}`;
        if (img && mainImg) {
          main.classList.add('is-swapping');
          setTimeout(() => {
            mainImg.src = img;
            requestAnimationFrame(() => main.classList.remove('is-swapping'));
          }, 200);
        }
      });
    });
  }

  // ===== PDP NOTIFY-WHEN-RELEASED EMAIL EXPAND =====
  function initPdpNotify(root) {
    root.querySelectorAll('[data-cr-notify-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('aria-controls');
        const target = id && root.querySelector('#' + CSS.escape(id));
        if (!target) return;
        const isOpen = !target.hasAttribute('hidden');
        if (isOpen) {
          target.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          target.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
          const input = target.querySelector('input[type="email"]');
          if (input) input.focus();
        }
      });
    });
  }

  // ===== STRIP SHOPIFY GENERATOR META =====
  // Removes <meta name="generator" content="Shopify"> after page parse.
  // Cosmetic — Shopify is still detectable via headers/CDN/checkout — but no point
  // volunteering the platform name in the response body. Done in JS because Shopify's
  // theme validator rejects any modification of {{ content_for_header }} in layout/theme.liquid.
  function stripGeneratorMeta() {
    document.querySelectorAll('meta[name="generator"]').forEach((el) => {
      const v = (el.getAttribute('content') || '').toLowerCase();
      if (v.indexOf('shopify') !== -1) el.remove();
    });
  }

  // ===== FORM SUBMIT DEBOUNCE =====
  // Disables submit buttons + blocks duplicate submissions for 2.5s. This catches
  // double-clicks and frustrates simple bots that hammer the same form.
  // Honeypot: any form with a filled `cr_hp_field` input is silently dropped — bots
  // typically fill every field they see, real users never see the hidden one.
  // ===== LIVE CLOCKS (substrip ATL / LAX) =====
  // Replaces the placeholder text in `.cr-clock` spans with live time.
  // Updates immediately, then every minute. Browsers without IANA timezone support
  // (very old) fall back silently to local time. Cleanup function returned so the
  // theme-editor section unload can clear the interval.
  function initClocks(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const clocks = scope.querySelectorAll('.cr-clock');
    if (clocks.length === 0) return null;
    function tick() {
      const now = new Date();
      clocks.forEach(function (el) {
        const tz = el.dataset.tz;
        const prefix = el.dataset.prefix || '';
        let timeStr;
        try {
          timeStr = now.toLocaleTimeString('en-US', {
            timeZone: tz,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        } catch (e) {
          timeStr = now.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
        el.textContent = (prefix ? prefix + ' ' : '') + timeStr;
      });
    }
    tick();
    const id = setInterval(tick, 60000);
    return function () { clearInterval(id); };
  }

  function initFormGuards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const forms = scope.querySelectorAll('form');
    forms.forEach((form) => {
      // Only guard forms that include the honeypot (newsletter, notify, contact).
      // Cart form + product ATC form have NO honeypot — bypass entirely so the
      // capture-phase submit-button disable below doesn't interfere with the
      // browser constructing the cart's Checkout submission (was reloading /cart).
      if (!form.querySelector('input[name="cr_hp_field"]')) return;

      if (form.dataset.crGuarded) return;
      form.dataset.crGuarded = '1';

      form.addEventListener('submit', (e) => {
        const honeypot = form.querySelector('input[name="cr_hp_field"]');
        if (honeypot && honeypot.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
        if (form.dataset.crSubmitting === '1') {
          e.preventDefault();
          return false;
        }
        form.dataset.crSubmitting = '1';
        const submits = form.querySelectorAll('button[type="submit"], input[type="submit"]');
        submits.forEach((btn) => {
          btn.dataset.crOrigDisabled = btn.disabled ? '1' : '0';
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        });
        // Re-enable after 2.5s in case the form failed validation client-side and
        // the user needs to retry. Real submissions will navigate away first.
        setTimeout(() => {
          form.dataset.crSubmitting = '';
          submits.forEach((btn) => {
            if (btn.dataset.crOrigDisabled !== '1') btn.disabled = false;
            btn.removeAttribute('aria-busy');
          });
        }, 2500);
      }, true);
    });
  }

  // ===== CART COUNT LISTENER =====
  function patchCartCount(count) {
    document.querySelectorAll('.cr-nav-cart').forEach(el => {
      el.textContent = `Cart · ${count}`;
    });
  }
  document.addEventListener('cart:updated', (e) => {
    const c = (e.detail && e.detail.cart && e.detail.cart.item_count);
    if (typeof c === 'number') patchCartCount(c);
  });


  // ===== INIT =====
  function initAll(root) {
    root = root || document;
    const cleanup = initInteractiveMark(root);
    if (cleanup && root.nodeType === 1) {
      sectionCleanups.set(root, cleanup);
    }
    initTypeIn(root);
    initScrambleOnView(root);
    initScrambleOnHover(root);
    initMobileToggle(root);
    initPdpGallery(root);
    initPdpNotify(root);
    initFormGuards(root);
    const clockCleanup = initClocks(root);
    if (clockCleanup && root.nodeType === 1) {
      const existing = sectionCleanups.get(root);
      sectionCleanups.set(root, function () {
        if (existing) existing();
        clockCleanup();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll(document);
      stripGeneratorMeta();
    });
  } else {
    initAll(document);
    stripGeneratorMeta();
  }

  // Theme editor: re-init when a section reloads
  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });

  // Theme editor: clean up rAF loops + listeners when a section is removed
  document.addEventListener('shopify:section:unload', (e) => {
    const cleanup = sectionCleanups.get(e.target);
    if (cleanup) {
      try { cleanup(); } catch (err) { console.warn('camprigid section cleanup failed', err); }
      sectionCleanups.delete(e.target);
    }
  });
})();
